import React from 'react';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import Icon from '@material-ui/core/Icon';
import Input from '@material-ui/core/Input';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { LCAlert } from '../../../../components/alert';
import { Dialog } from '../../../../components/dialog';
import { match, RouteComponentProps } from 'react-router-dom';

// Components
import App from '../../../../components/app';
import ErrorMessage from '../../../../components/error-message';
import EnhancedForm from '../../../../components/form';
import { FormDatas, FormPickerDatas } from '../../../../local-types';
import { TooltipButton } from '../../../../components/tooltip-button';
import Listing from '../../../../components/listing';
import { PricingPricesAddTariffHoroPage } from '../addTariffHoro/prices-add-tariff-horo';
// Modules
import { ErrorPayload, selectors as errorSelectors } from '../../../../redux/reducers/errors';
import {
    actions as pricingActions,
    selectors as pricingSelectors,
    types as pricingTypes,
} from '../../../../redux/reducers/pricing';
import { selectors as pendingSelectors } from '../../../../redux/reducers/pendingOperations';
import { Area, AreaByID, Tariff, TariffArrayByID, TariffByID, TParams } from '../../../../redux/reducers/api-types';
// Ressources
import FormUtils from '../../../../utils/forms';
import withGlobalStyles from '../../../../utils/global-styles';
import ObjectUtils from '../../../../utils/objectUtils';
import { ChangeEvent, FormEvent } from '../../../../utils/typeUtils';
import DateUtils from '../../../../utils/dateUtils';
import UserUtils, { AccountRight, RightRank } from '../../../../utils/userUtils';
import { paths } from '../../../paths';
import { ActionLogs } from '../../../../components/action-logs';
import { clsx } from '../../../../utils/htmlUtils';
import { formDefaultConf, generateSurgeOptions } from '../shared';

const queryString = require('query-string');

interface PricesAddTariffForfaitProps extends WithTranslation, RouteComponentProps {
    match: match<TParams>;
    globalClasses: any;
    tariffs: TariffByID;
    tariffsList: TariffArrayByID;
    areas: AreaByID;
    getPricesAddTariffError: ErrorPayload;
    isSavingTariff: boolean;
    getTariff: (id: string) => void;
    createTariff: (payload: any, callback?: Function) => void;
    editTariff: (id: string, payload: any, callback?: Function) => void;
    deleteTariff: (idp: string, idt: any) => void;
    getAreas: () => void;
    searchTariffs: (payload: any, callback?: Function, onError?: Function) => void;
}

interface PricingPricesAddTariffForfaitState {
    formData: any;
    formMinPrice: any;
    formMinPriceInit: any;
    bothWaysMessage: string;
}

// One direction to save: the tariff is updated when it already exists, created otherwise
interface TariffToSave {
    id?: string;
    payload: any;
}

class PricingPricesAddTariffForfaitPage extends React.Component<
    PricesAddTariffForfaitProps,
    PricingPricesAddTariffForfaitState
> {
    alertRef: any;
    dialogRef: any;
    optionsSurge: FormPickerDatas[];
    pendingSave: { current: TariffToSave; reverse: TariffToSave };

    isAdmin: boolean;

    constructor(props: PricesAddTariffForfaitProps) {
        super(props);
        const { t, match, location } = props;
        const id = match.params.id;
        const { edit_id, both_ways } = queryString.parse(location.search);
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.PRICING_MGMT, mode: RightRank.W });
        this.state = {
            formMinPrice: ObjectUtils.deepCopy(formDefaultConf),
            formMinPriceInit: ObjectUtils.deepCopy(formDefaultConf),
            bothWaysMessage: '',
            formData: {
                type: 'flat_rate',
                pricing_set_id: id,
                // A flat rate is almost always the same both ways, so it is proposed by default on creation.
                // On edition it is only checked when coming from a tariff already defined in both ways.
                both_ways: !edit_id || both_ways === '1',
                pricing_params: {
                    time_settings: {
                        asap: {
                            is_allowed: 'on',
                        },
                        prebook: {
                            is_allowed: 'on',
                            min_delay: 0,
                        },
                    },
                    flat_price: [],
                    surge: {
                        asap: {},
                        prebook: {},
                    },
                },
                // "start_area_id": "3a839ff6-7a64-454e-a398-e349d94d0f9b",
                // "end_area_id": "fccdfb53-ca6e-404d-92ac-9b325bf11127",
                // "is_surge_asap": true,
                // "is_surge_prebook": true,
                // "pricing_params": {
                //   "flat_price": PricingPricesAddTariffHoroPage.orderArray([{
                //     "time_slot": { "fri": [{ "start": "09:00", "end": "15:00" }] },
                //     "value": 10
                //   }])
                // }
            },
        };

        this.optionsSurge = generateSurgeOptions(t);
    }

    componentDidMount() {
        const { location, match, getTariff, getAreas, searchTariffs } = this.props;
        const { edit_id } = queryString.parse(location.search);
        if (edit_id) {
            getTariff(edit_id);
        }
        getAreas();
        // Needed to know if the return tariff already exists
        searchTariffs({ pricing_set_id: match.params.id });
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.match.params.id !== (this.props.match.params as any).id) {
            const id = this.props.match.params.id;
            this.props.getTariff(id);
        }
    }

    static getDerivedStateFromProps(props: PricesAddTariffForfaitProps, state: PricingPricesAddTariffForfaitState) {
        const { edit_id, both_ways } = queryString.parse(props.location.search);
        const { tariffs } = props;
        const { formData } = state;

        if (!formData.loaded && tariffs && tariffs[edit_id]) {
            const formatedData: any = ObjectUtils.deepCopy(tariffs[edit_id]);
            // The loaded tariff overrides the whole form, the both ways choice has to be applied again
            formatedData.both_ways = both_ways === '1';
            formatedData.pricing_params.flat_price = PricingPricesAddTariffHoroPage.orderArray(
                formatedData.pricing_params.flat_price,
            );

            const prebook = formatedData?.pricing_params?.surge?.prebook;
            const asap = formatedData?.pricing_params?.surge?.asap;

            if (prebook?.warranty_price === 0) {
                prebook.surge_no_variation = 'off';
            } else if (prebook?.warranty_price > 0) {
                prebook.surge_no_variation = 'on';
                prebook.surge_no_variation_price = prebook.warranty_price;
            }

            if (prebook?.is_enabled === 0) {
                prebook.is_enabled = 'off';
            } else if (prebook?.is_enabled === 1) {
                prebook.is_enabled = 'on';
            }

            if (asap?.is_enabled === 0) {
                asap.is_enabled = 'off';
            } else if (asap?.is_enabled === 1) {
                asap.is_enabled = 'on';
            }

            const time_settings = formatedData?.pricing_params?.time_settings;
            if (time_settings) {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const { asap, prebook } = time_settings;

                if (prebook?.is_allowed === 0) {
                    prebook.is_allowed = 'off';
                } else if (prebook?.is_allowed === 1) {
                    prebook.is_allowed = 'on';
                }

                if (prebook?.min_delay > 0) {
                    prebook.min_delay /= 60;
                }

                if (asap?.is_allowed === 0) {
                    asap.is_allowed = 'off';
                } else if (asap?.is_allowed === 1) {
                    asap.is_allowed = 'on';
                }
            }

            formatedData.loaded = true;
            return { formData: formatedData };
        }
        return null;
    }

    //========================================//
    //=============== Handlers ===============//
    //========================================//

    handleChipsActivate = (index) => (e) => {
        e.stopPropagation();
        e.preventDefault();
        const { formMinPrice } = this.state;
        formMinPrice.days[index].value = !formMinPrice.days[index].value;
        this.setState({
            formMinPrice,
        });
    };

    handleChange = (event: ChangeEvent) => {
        const { formMinPrice } = this.state;
        this.setState({
            formMinPrice: FormUtils.nextSubState(event, formMinPrice),
        });
    };

    handleDelete = (index) => (e) => {
        const { formData } = this.state;

        formData.pricing_params.flat_price.splice(index, 1);
        this.setState({
            formData,
        });
    };

    handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const { formData, formMinPrice, formMinPriceInit } = this.state;

        if (!formData.pricing_params?.flat_price) {
            formData.pricing_params = { ...formData.pricing_params, flat_price: [] };
        }
        formData.pricing_params.flat_price.push(Object.assign({}, formMinPrice));

        this.setState({
            formData,
            formMinPrice: Object.assign({}, formMinPriceInit),
        });
    };

    handleTariffReset = () => {
        const { formData } = this.state;
        formData.pricing_params = { ...formData.pricing_params, flat_price: [] };

        this.setState({
            formData,
        });
    };

    //========================================//
    //=============== Builders ===============//
    //========================================//

    generateInputs = (): FormDatas[] => {
        const { t, areas, match } = this.props;
        const id = match.params.id;
        const inputs: FormDatas[] = [
            {
                title: t(id ? 'tariffs.edit_forfait' : 'tariffs.new_forfait'),
                datas: [
                    {
                        id: 'start_area_id',
                        label: t('tariffs.start_area'),
                        type: 'picker',
                        searchable: true,
                        required: true,
                    },
                    {
                        id: 'end_area_id',
                        label: t('tariffs.end_area'),
                        type: 'picker',
                        searchable: true,
                        required: true,
                    },
                    {
                        id: 'both_ways',
                        label: t('tariffs.both_ways'),
                        type: 'boolean',
                        hint: t('tariffs.both_ways_hint'),
                    },
                ],
            },
            {
                title: t('tariffs.surge'),
                datas: [
                    {
                        id: 'pricing_params.surge.asap.is_enabled',
                        label: t('tariffs.surge_asap'),
                        type: 'picker',
                        options: this.optionsSurge,
                        noEmpty: true,
                    },
                    {
                        id: 'pricing_params.surge.prebook.is_enabled',
                        label: t('tariffs.surge_prebook'),
                        type: 'picker',
                        options: this.optionsSurge,
                        noEmpty: true,
                    },
                    {
                        id: 'pricing_params.surge.prebook.surge_no_variation',
                        label: t('tariffs.surge_no_variation'),
                        type: 'picker',
                        options: this.optionsSurge,
                        noEmpty: true,
                    },
                    {
                        id: 'pricing_params.surge.prebook.surge_no_variation_price',
                        label: t('tariffs.surge_no_variation_price'),
                        type: 'number',
                        hint: t('tariffs.surge_no_variation_price_hint'),
                        inputsAttributs: { step: 0.01, min: 0 },
                        required: true,
                        linked: { id: 'pricing_params.surge.prebook.surge_no_variation', value: 'on' },
                    },
                ],
            },
            {
                title: t('tariffs.time_settings.title'),
                datas: [
                    {
                        id: 'pricing_params.time_settings.asap.is_allowed',
                        label: t('tariffs.time_settings.asap'),
                        type: 'picker',
                        options: this.optionsSurge,
                        noEmpty: true,
                    },
                    {
                        id: 'pricing_params.time_settings.prebook.is_allowed',
                        label: t('tariffs.time_settings.prebook'),
                        type: 'picker',
                        options: this.optionsSurge,
                        noEmpty: true,
                    },
                    {
                        id: 'pricing_params.time_settings.prebook.min_delay',
                        label: t('tariffs.time_settings.prebook_min_delay'),
                        type: 'number',
                        inputsAttributs: { step: 0.01, min: 0 },
                        linked: { id: 'pricing_params.time_settings.prebook.is_allowed', value: 'on' },
                    },
                ],
            },
            {
                title: t('tariffs.flat_price'),
                datas: [
                    {
                        id: 'pricing_params.flat_price',
                        label: t('tariffs.flat_price'),
                        type: 'create',
                        customView: (val, handleChange) => {
                            const { formData, formMinPrice } = this.state;
                            const { globalClasses } = this.props;
                            return (
                                <div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ display: 'flex', margin: 'auto', marginBottom: 30 }}>
                                            {DateUtils.generateDays(t).map((d, index) => {
                                                const activated = formMinPrice.days[index].value;
                                                return (
                                                    <Chip
                                                        key={d.id}
                                                        icon={null}
                                                        color={activated ? 'secondary' : 'default'}
                                                        label={d.label}
                                                        onClick={this.handleChipsActivate(index)}
                                                        style={{ marginRight: 8 }}
                                                        // className={classes.chip}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                marginBottom: 50,
                                            }}
                                        >
                                            <span style={{ margin: 'auto' }}>{t('common:from')}</span>
                                            <FormControl key={'start'} margin="dense">
                                                <Input
                                                    id={'start'}
                                                    type={'time'}
                                                    inputProps={{ form: 'form_flat_price' }}
                                                    placeholder={'_:_'}
                                                    value={formMinPrice.start ?? ''}
                                                    name={`start`}
                                                    required={true}
                                                    onChange={this.handleChange}
                                                />
                                            </FormControl>
                                            <span style={{ margin: 'auto' }}>{t('common:hour_to')}</span>
                                            <FormControl key={'end'} margin="dense">
                                                <Input
                                                    id={'end'}
                                                    inputProps={{ form: 'form_flat_price' }}
                                                    type={'time'}
                                                    placeholder={'_:_'}
                                                    value={formMinPrice.end ?? ''}
                                                    name={`end`}
                                                    required={true}
                                                    onChange={this.handleChange}
                                                />
                                            </FormControl>
                                            <span style={{ margin: 'auto' }}>{t('common:price')}</span>
                                            <FormControl key={'value'} margin="dense">
                                                <Input
                                                    id={'value'}
                                                    type={'number'}
                                                    inputProps={{
                                                        form: 'form_flat_price',
                                                        min: 0,
                                                        step: 0.01,
                                                    }}
                                                    placeholder={'10'}
                                                    value={formMinPrice.value ?? ''}
                                                    name={`value`}
                                                    required={true}
                                                    onChange={this.handleChange}
                                                />
                                            </FormControl>
                                            <span style={{ margin: 'auto' }}>{t('common:euro_ht')}</span>
                                            <TooltipButton title={t('common:add')}>
                                                <Button
                                                    aria-label="filter list"
                                                    type="submit"
                                                    variant="contained"
                                                    form="form_flat_price"
                                                    color={
                                                        formMinPrice.start &&
                                                        formMinPrice.end &&
                                                        formMinPrice.value !== null &&
                                                        String(formMinPrice.value).length > 0
                                                            ? 'secondary'
                                                            : 'inherit'
                                                    }
                                                >
                                                    <Icon fontSize="small">add</Icon>
                                                </Button>
                                            </TooltipButton>
                                        </div>
                                    </div>
                                    <Listing
                                        datas={formData?.pricing_params?.flat_price?.map(
                                            ({ days, end, start, value }, index) => {
                                                const sortedDays = ObjectUtils.sortByDay(days);
                                                const strDays = sortedDays
                                                    .filter((d) => d.value)
                                                    .map(({ short }, dindex) => t('common:days.' + short))
                                                    .join(', ');
                                                return (
                                                    <div
                                                        key={`${value} ${strDays} ${start} ${end}`}
                                                        style={{
                                                            flex: 'inherit',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <div style={{ marginRight: 6, flex: 4 }}>
                                                            <b>{strDays}</b>
                                                            <br />
                                                            <span>
                                                                {`${t('common:hour_from')} ${start} 
                                                                    ${t('common:hour_to')} ${end}`}
                                                            </span>
                                                        </div>
                                                        <div style={{ marginRight: 6, flex: 1 }}>
                                                            <b>{`${value} ${t('common:euro_ht')}`}</b>
                                                        </div>
                                                        {this.isAdmin && (
                                                            <TooltipButton title={t('common:delete')}>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    color="primary"
                                                                    onClick={this.handleDelete(index)}
                                                                    className={clsx(
                                                                        globalClasses.btnAutoWidth,
                                                                        globalClasses.btnDanger,
                                                                    )}
                                                                >
                                                                    <Icon fontSize="small">delete</Icon>
                                                                </Button>
                                                            </TooltipButton>
                                                        )}
                                                    </div>
                                                );
                                            },
                                        )}
                                    />
                                </div>
                            );
                        },
                    },
                ],
            },
        ];

        if (areas) {
            inputs[0].datas.find((d) => d.id === 'start_area_id').options = Object.values(areas).map((r: Area) => {
                return { id: r.id, name: r.name };
            });
            inputs[0].datas.find((d) => d.id === 'end_area_id').options = Object.values(areas).map((r: Area) => {
                return { id: r.id, name: r.name };
            });
        }

        return inputs;
    };

    //========================================//
    //=============== Both ways ==============//
    //========================================//

    findFlatRateTariff = (tariffs: Tariff[], startAreaId: string, endAreaId: string): Tariff =>
        tariffs.find((x) => x.type === 'flat_rate' && x.start_area?.id === startAreaId && x.end_area?.id === endAreaId);

    // Save the tariff and its return (end area -> start area) with the exact same configuration
    saveBothWays = (payload: any, editId: string, startAreaId: string, endAreaId: string): void => {
        const { match, tariffsList, searchTariffs } = this.props;
        // Refresh the tariffs before deciding what has to be created, to never duplicate an existing tariff
        searchTariffs(
            { pricing_set_id: match.params.id },
            (dispatch, response) =>
                this.prepareBothWays(payload, editId, startAreaId, endAreaId, response?.payload?.data ?? []),
            () => this.prepareBothWays(payload, editId, startAreaId, endAreaId, tariffsList?.[match.params.id] ?? []),
        );
    };

    prepareBothWays = (
        payload: any,
        editId: string,
        startAreaId: string,
        endAreaId: string,
        tariffs: Tariff[],
    ): void => {
        const { t, areas } = this.props;
        const current: Tariff = editId ? null : this.findFlatRateTariff(tariffs, startAreaId, endAreaId);
        const reverse: Tariff = this.findFlatRateTariff(tariffs, endAreaId, startAreaId);

        this.pendingSave = {
            current: { id: editId ?? current?.id, payload },
            reverse: {
                id: reverse?.id,
                payload: {
                    type: payload.type,
                    pricing_set_id: payload.pricing_set_id,
                    start_area_id: endAreaId,
                    end_area_id: startAreaId,
                    pricing_params: ObjectUtils.deepCopy(payload.pricing_params),
                },
            },
        };

        // Both tariffs already exist: nothing is created, no need to warn the user
        if (editId && reverse) {
            this.runPendingSave();
            return;
        }

        const areaName = (id: string): string => areas?.[id]?.name ?? id;
        const direction = (start: string, end: string, exists: boolean): string =>
            t(exists ? 'tariffs.both_ways_confirm_update' : 'tariffs.both_ways_confirm_create', {
                start: areaName(start),
                end: areaName(end),
            });

        this.setState(
            {
                bothWaysMessage: [
                    direction(startAreaId, endAreaId, !!this.pendingSave.current.id),
                    direction(endAreaId, startAreaId, !!reverse),
                    '',
                    t('tariffs.both_ways_confirm_question'),
                ].join('\n'),
            },
            () => this.dialogRef?.showDialog(),
        );
    };

    runPendingSave = (): void => {
        const { createTariff, editTariff } = this.props;
        const pending = this.pendingSave;
        if (!pending) {
            return;
        }
        this.pendingSave = null;

        // The return tariff is saved last, its success redirects to the pricing set
        const saveReverse = () => {
            const { id, payload } = pending.reverse;
            if (id) {
                editTariff(id, payload);
            } else {
                createTariff(payload);
            }
        };

        if (pending.current.id) {
            editTariff(pending.current.id, pending.current.payload, saveReverse);
        } else {
            createTariff(pending.current.payload, saveReverse);
        }
    };

    formatSurgePickerToBoolean = (value: any) => {
        if (value === 'on') {
            return true;
        } else if (value === 'off') {
            return false;
        } else {
            return null;
        }
    };

    onSubmitTariff = (formData: any, event) => {
        if (!this.isAdmin) {
            return;
        }

        // Apply custom field min_price to the formData
        formData.pricing_params.flat_price = this.state.formData.pricing_params?.flat_price;

        // let data = new FormData(event.target);
        this.setState({
            formData: formData,
        });

        const formatedData = ObjectUtils.deepCopy(formData);
        formatedData.pricing_params.flat_price = PricingPricesAddTariffHoroPage.orderArray(
            formatedData.pricing_params.flat_price,
            true,
        );

        if (!formatedData.pricing_params?.flat_price || formatedData.pricing_params.flat_price.length === 0) {
            this.alertRef.showAlert();
        } else {
            this.alertRef.closeAlert();

            // Format surge picker
            if (formatedData.pricing_params.surge?.prebook) {
                formatedData.pricing_params.surge.prebook.is_enabled = this.formatSurgePickerToBoolean(
                    formatedData.pricing_params.surge.prebook.is_enabled,
                );
            }
            if (formatedData.pricing_params.surge?.asap) {
                formatedData.pricing_params.surge.asap.is_enabled = this.formatSurgePickerToBoolean(
                    formatedData.pricing_params.surge.asap.is_enabled,
                );
            }

            // Format surge warranty_price
            if (formatedData.pricing_params.surge?.prebook?.surge_no_variation === 'on') {
                formatedData.pricing_params.surge.prebook.warranty_price =
                    formatedData.pricing_params.surge.prebook.surge_no_variation_price;
            } else if (formatedData.pricing_params.surge?.prebook?.surge_no_variation === 'off') {
                formatedData.pricing_params.surge.prebook.warranty_price = 0;
            } else if (formatedData.pricing_params.surge?.prebook?.warranty_price >= 0) {
                delete formatedData.pricing_params.surge.prebook.warranty_price;
            }

            if (formatedData.pricing_params.surge?.prebook?.surge_no_variation) {
                delete formatedData.pricing_params.surge.prebook.surge_no_variation;
            }
            if (formatedData.pricing_params.surge?.prebook?.surge_no_variation_price) {
                delete formatedData.pricing_params.surge.prebook.surge_no_variation_price;
            }

            // Format time_settings picker
            if (formatedData.pricing_params.time_settings?.prebook) {
                const prebook = formatedData.pricing_params.time_settings.prebook;
                prebook.is_allowed = PricingPricesAddTariffHoroPage.formatSurgePickerToBoolean(prebook.is_allowed);
                if (!prebook.is_allowed) {
                    delete prebook.min_delay;
                } else {
                    if (prebook.min_delay > 0) {
                        prebook.min_delay *= 60;
                    }
                }
            }
            if (formatedData.pricing_params.time_settings?.asap) {
                formatedData.pricing_params.time_settings.asap.is_allowed =
                    PricingPricesAddTariffHoroPage.formatSurgePickerToBoolean(
                        formatedData.pricing_params.time_settings.asap.is_allowed,
                    );
            }

            const bothWays: boolean = !!formatedData.both_ways;

            delete formatedData.loaded;
            delete formatedData.history;
            delete formatedData.both_ways; // Only used by the form, never sent to the API
            ObjectUtils.cleanEmpty(formatedData);

            const { edit_id } = queryString.parse(this.props.location.search);
            // On edition the areas may only be known through their object
            const startAreaId: string = formatedData.start_area_id ?? formatedData.start_area?.id;
            const endAreaId: string = formatedData.end_area_id ?? formatedData.end_area?.id;

            if (bothWays && startAreaId && endAreaId && startAreaId !== endAreaId) {
                this.saveBothWays(formatedData, edit_id, startAreaId, endAreaId);
            } else if (edit_id) {
                this.props.editTariff(edit_id, formatedData);
            } else {
                this.props.createTariff(formatedData);
            }
        }
    };

    render() {
        const { formData, bothWaysMessage } = this.state;
        const { t, location, match, getPricesAddTariffError, tariffs, isSavingTariff } = this.props;
        const { edit_id } = queryString.parse(location.search);

        const id: string = match.params.id;
        const formPayload = edit_id ? formData : { ...formData, loaded: true };

        return (
            <App needRights={{ name: AccountRight.PRICING_MGMT, mode: RightRank.R }}>
                <ErrorMessage payload={getPricesAddTariffError} />
                <LCAlert
                    onRef={(ref) => (this.alertRef = ref)}
                    details={t('tariffs.missing_price_forfait')}
                    type="error"
                    open={false}
                />
                <Dialog
                    onRef={(ref) => (this.dialogRef = ref)}
                    dialogTitle={t('tariffs.both_ways_confirm_title')}
                    description={bothWaysMessage}
                    customButton={<></>}
                    onOK={this.runPendingSave}
                    onKO={() => (this.pendingSave = null)}
                />
                {formPayload?.loaded && (
                    <>
                        <form onSubmit={this.handleSubmit} id="form_flat_price">
                            <input type="submit" hidden value="Ok" />
                        </form>
                        <EnhancedForm
                            payload={formPayload}
                            inputs={this.generateInputs()}
                            backRoute={`${paths.pricingManagePrices}/${id}`}
                            onReset={this.handleTariffReset}
                            onSubmit={this.onSubmitTariff}
                            readOnly={!this.isAdmin}
                            actionBarHidden={!this.isAdmin}
                            loader={isSavingTariff}
                        />
                        <ActionLogs history={tariffs?.[edit_id]?.history} withValues allValues />
                    </>
                )}
            </App>
        );
    }
}

const PricingPricesAddTariffForfaitPageWithTranslation = withGlobalStyles(
    withTranslation(['pricing', 'common'])(PricingPricesAddTariffForfaitPage),
);

const mapStateToProps = (state) => ({
    areas: pricingSelectors.areas(state),
    tariffs: pricingSelectors.tariffs(state),
    tariffsList: pricingSelectors.tariffs_list(state),
    getPricesAddTariffError: errorSelectors.getPricesAddTariffError(state),
    isSavingTariff: pendingSelectors.isPending(
        state,
        pricingTypes.CREATE_TARIFF,
        pricingTypes.EDIT_TARIFF,
        pricingTypes.SEARCH_TARIFFS,
    ),
});

const mapDispatchToProps = {
    getTariff: pricingActions.getTariff,
    createTariff: pricingActions.createTariff,
    editTariff: pricingActions.editTariff,
    deleteTariff: pricingActions.deleteTariff,
    getAreas: pricingActions.getAreas,
    searchTariffs: pricingActions.searchTariffs,
};

export default connect(mapStateToProps, mapDispatchToProps)(PricingPricesAddTariffForfaitPageWithTranslation);
